import React, { useState, useMemo } from "react";
import styles from "./styles.module.scss";
import closeIcon from "app/images/close-icon.svg";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import { useTranslation } from "react-i18next";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import { GroupPermissions } from "app/models/GroupsModel";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { useActions } from "app/utils/hooks";
import {
  addTeamGroupSettings,
  editTeamGroupSettings,
  fetchTeamGroupData,
} from "app/actions/team-group";
import { TeamGroupModel } from "app/models";
import ChannelsSelect from "../ChannelsSelect";
import { RequestError } from "typedefs";

interface IChannelPermissionsModal {
  isOpen?: boolean;
  onCloseFunc?: () => void;
  onSaveChannelPermissions: (permissions: GroupPermissions) => void;
  channelDataProps?: GroupPermissions;
  onErrorSavingPermissions?: (err: RequestError) => void;
  excludeChannelIds?: string[];
}

const ChannelPermissionsModal = ({
  isOpen,
  onCloseFunc,
  onSaveChannelPermissions,
  channelDataProps,
  onErrorSavingPermissions,
  excludeChannelIds,
}: IChannelPermissionsModal) => {
  const { t } = useTranslation();
  const channelActions = useActions({
    fetchTeamGroupData,
    addTeamGroupSettings,
    editTeamGroupSettings,
  });

  const [selectedChannelId, setSelectedChannelId] = useState(
    channelDataProps?.teamGroupId,
  );
  const [channelData, setChannelData] = useState({
    id: channelDataProps?.id,
    membersCanPost: channelDataProps?.membersCanPost ?? true,
    membersCanReply: channelDataProps?.membersCanReply ?? true,
    membersCanViewOtherMembers:
      channelDataProps?.membersCanViewOtherMembers ?? true,
    membersCanSeePostData: channelDataProps?.membersCanSeePostData ?? true,
  });

  const [pendingUpdate, setPendingUpdate] = useState(false);

  const handleToggleChange = async (key: keyof TeamGroupModel) => {
    if (!channelData.id && !selectedChannelId) {
      return;
    }

    const updatedValue = !channelData[key];

    const updatedChannel = {
      ...channelData,
      [key]: updatedValue,
    };

    setChannelData(updatedChannel);
  };

  const channelSelectHandler = async (channelId: string) => {
    setSelectedChannelId(channelId);
  };

  const AllowList = useMemo(
    () => [
      {
        label: t("Allow members to create posts"),
        value: "membersCanPost",
        isActive: channelData?.membersCanPost ?? true,
      },
      {
        label: t("Allow members to reply to posts"),
        value: "membersCanReply",
        isActive: channelData?.membersCanReply ?? true,
      },
      {
        label: t("Allow members to view other members"),
        value: "membersCanViewOtherMembers",
        isActive: channelData?.membersCanViewOtherMembers ?? true,
      },
      {
        label: t("Allow members to see post Views/Likes"),
        value: "membersCanSeePostData",
        isActive: channelData?.membersCanSeePostData ?? true,
      },
    ],
    [channelData, t],
  );

  const onCancel = () => {
    onCloseFunc();
  };

  const onDone = async () => {
    setPendingUpdate(true);
    try {
      let response: GroupPermissions | undefined;

      if (channelDataProps?.teamGroupId) {
        response = await channelActions.editTeamGroupSettings({
          teamId: channelDataProps.teamGroupId,
          params: { ...channelData },
        });
      } else {
        response = await channelActions.addTeamGroupSettings({
          teamId: selectedChannelId,
          params: { ...channelData },
        });
      }

      if (response) {
        onSaveChannelPermissions?.(response);
      }
      setPendingUpdate(false);
    } catch (err) {
      console.error("Couldn't update channel permission:", err);
      onErrorSavingPermissions(err);
      setPendingUpdate(false);
    }
  };

  return (
    <ModalLayout
      classNames={styles.modal_wrapper}
      onClose={onCloseFunc}
      isOpen={isOpen}
    >
      <div className={styles.content_wrapper}>
        <div className={styles.content}>
          <div className={styles.modal_header}>
            <h1>
              {channelDataProps?.id
                ? t("Edit Permissions")
                : t("Add Permissions")}
            </h1>
            <img
              src={closeIcon}
              alt="close"
              className={styles.close_icon}
              onClick={onCloseFunc}
            />
          </div>
          <div className={styles.content_container}>
            <label className={styles.label}>{t("Channel")}</label>
            <ChannelsSelect
              disabled={!!channelDataProps?.teamGroupId}
              value={selectedChannelId}
              onChange={channelSelectHandler}
              placeholder={t("Select Channel")}
              excludeIds={excludeChannelIds}
            />

            {selectedChannelId &&
              AllowList.map((item) => {
                return (
                  <SwitchToggle
                    key={item.value}
                    checked={item.isActive}
                    onChange={() =>
                      handleToggleChange(item.value as keyof GroupPermissions)
                    }
                    title={item.label}
                    disabled={pendingUpdate}
                  />
                );
              })}
          </div>
        </div>
        <div className={styles.footer_buttons}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={onCancel}
            disabled={pendingUpdate}
          />
          <ButtonVariant
            title={t("Done")}
            buttonType="primary"
            onClickFunc={onDone}
            disabled={pendingUpdate}
          />
        </div>
      </div>
    </ModalLayout>
  );
};

export default ChannelPermissionsModal;
